import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import { Box, Button, Typography, Stack } from "@mui/material";
import useResponsive from "../hooks/useResponsive";
import Logo from "../components/Logo";
import PageNotFoundIllustration from "../components/PageNotFoundIllustration";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(8, 2, 2, 2),
  overflowY: "auto",
  [theme.breakpoints.up("md")]: {
    width: "35%",
    alignItems: "center",
    padding: theme.spacing(3),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  textAlign: "center", // Center content for the 404 page
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 5),
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

export default function Page404() {
  const navigate = useNavigate();
  const mdUp = useResponsive("up", "md");

  return (
    <>
      <Helmet>
        <title>Contactie | Page Not Found</title>
        <meta
          name="description"
          content="The page you are looking for does not exist on Contactie."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <StyledPageContainer>
        <ContentWrapper>
          <Box
            component="header"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              zIndex: 10,
              top: 40,
              left: 40,
            }}
          >
            <Logo sx={{ height: 40, width: "auto" }} />
          </Box>

          {mdUp && (
            <StyledSection>
              <Typography variant="h4" sx={{ mb: 2, px: 5 }}>
                Oops! Page not found.
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                We can't seem to find the page you're looking for. Let's help
                you get back on track.
              </Typography>
              <PageNotFoundIllustration
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  maxHeight: "50vh",
                  objectFit: "contain",
                  mt: 8,
                }}
              />
            </StyledSection>
          )}

          <StyledContent>
            <FormContainer>
              {/* --- Mobile Header --- */}
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Logo sx={{ height: 40, width: "auto", mb: 3 }} />
                <PageNotFoundIllustration sx={{ height: 180 }} />
              </Box>

              {/* --- Content Area --- */}
              <Box>
                <Typography variant="h3" paragraph sx={{ fontWeight: 700 }}>
                  Page Not Found
                </Typography>
                <Typography sx={{ color: "text.secondary", mb: 4 }}>
                  The page you are looking for might have been removed, had its
                  name changed, or is temporarily unavailable.
                </Typography>

                <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                  <Button
                    sx={{ flexGrow: 1 }}
                    size="large"
                    variant="contained"
                    onClick={() => navigate("/Login")}
                  >
                    Go to Home
                  </Button>
                  {/* <Button
                    sx={{ flexGrow: 1 }}
                    size="large"
                    variant="outlined"
                    onClick={() => navigate(-1)}
                  >
                    Go Back
                  </Button> */}
                </Stack>
              </Box>
            </FormContainer>
          </StyledContent>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
